import type { TagMatch } from 'components/TagMatchToggle'
import {
  API_REPORTS,
  API_REPORTS_BALLOON_NOTICES_CANCEL,
  API_REPORTS_BALLOON_NOTICES_CONFIG,
  API_REPORTS_BALLOON_NOTICES_DOWNLOAD,
  API_REPORTS_BALLOON_NOTICES_GENERATE,
  API_REPORTS_BALLOON_NOTICES_SCHEDULE,
  API_REPORTS_BALLOON_NOTICES_SCHEDULE_RUN,
  API_REPORTS_BALLOON_NOTICES_STATUS,
  API_REPORTS_BALLOON_NOTICES_TEMPLATE_PREVIEW,
  API_REPORTS_BALLOON_NOTICES_TEMPLATES,
  API_REPORTS_BY_ID,
  API_REPORTS_CSV_DOWNLOAD,
  API_REPORTS_DOWNLOAD,
  API_REPORTS_HMDA_PIPE_JOB,
  API_REPORTS_HMDA_PIPE_JOB_CANCEL,
  API_REPORTS_HMDA_PIPE_JOB_DOWNLOAD,
  API_REPORTS_HMDA_PIPE_JOBS,
  API_REPORTS_INSURANCE_NOTICES_CANCEL,
  API_REPORTS_INSURANCE_NOTICES_CONFIG,
  API_REPORTS_INSURANCE_NOTICES_DOWNLOAD,
  API_REPORTS_INSURANCE_NOTICES_GENERATE,
  API_REPORTS_INSURANCE_NOTICES_SCHEDULE,
  API_REPORTS_INSURANCE_NOTICES_SCHEDULE_RUN,
  API_REPORTS_INSURANCE_NOTICES_STATUS,
  API_REPORTS_INSURANCE_NOTICES_TEMPLATE_PREVIEW,
  API_REPORTS_INSURANCE_NOTICES_TEMPLATES,
  API_REPORTS_LATE_NOTICES_CANCEL,
  API_REPORTS_LATE_NOTICES_CONFIG,
  API_REPORTS_LATE_NOTICES_DOWNLOAD,
  API_REPORTS_LATE_NOTICES_GENERATE,
  API_REPORTS_LATE_NOTICES_PREVIEW,
  API_REPORTS_LATE_NOTICES_SCHEDULE,
  API_REPORTS_LATE_NOTICES_SCHEDULE_RUN,
  API_REPORTS_LATE_NOTICES_STATUS,
  API_REPORTS_LATE_NOTICES_TEMPLATE_PREVIEW,
  API_REPORTS_LATE_NOTICES_TEMPLATES,
  API_REPORTS_LOAN_DETAILS_FIELD_VALUES,
  API_REPORTS_LOAN_DISCOUNT,
  API_REPORTS_MCR_XML,
  API_REPORTS_MCR_XML_PREVIEW,
  API_REPORTS_PLACEHOLDERS,
  API_REPORTS_PREVIEW,
  API_REPORTS_RATE_NOTICES_CANCEL,
  API_REPORTS_RATE_NOTICES_DOWNLOAD,
  API_REPORTS_RATE_NOTICES_GENERATE,
  API_REPORTS_RATE_NOTICES_PREVIEW,
  API_REPORTS_RATE_NOTICES_STATUS,
  API_REPORTS_SERVICING,
  API_REPORTS_SERVICING_JOB,
  API_REPORTS_SERVICING_JOB_CANCEL,
  API_REPORTS_SERVICING_JOB_DOWNLOAD,
  API_REPORTS_SERVICING_JOBS,
  API_REPORTS_SERVICING_OPTIONS,
} from 'config'
import Api from 'services/api'

import { downloadFile } from '../documents'

// ---- Rate & Payment Change Notices (portfolio-wide) ----
export interface RateNoticeScope {
  /** `YYYY-MM-DD`. Changes effective on or after this date; defaults to today. */
  from?: string
  /** `YYYY-MM-DD`. Changes effective on or before it; defaults to today + the ARM notice lead. */
  to?: string
  fields?: Array<'rate' | 'payment' | 'escrow'>
  sources?: string[]
  tags?: string[]
  tagMatch?: TagMatch
  excludeTags?: string[]
  includeAlreadyEmailed?: boolean
  regenerate?: boolean
  email?: boolean
}

export interface RateNoticeTargetRow {
  loanNumber: number
  changeId: number
  loanNo: string
  borrower: string
  effectiveDate: string
  fields: string[]
  sources: string[]
  alreadyEmailed: boolean
  alreadyFiled: boolean
}

export interface RateNoticeSkippedRow {
  loanNumber: number
  loanNo: string
  borrower: string
  effectiveDate: string
  reason: string
}

export const previewRateNotices = (
  data: RateNoticeScope,
): Promise<{
  success: number
  window: { from: string; to: string }
  targets: RateNoticeTargetRow[]
  skipped: RateNoticeSkippedRow[]
}> => {
  return Api.post(API_REPORTS_RATE_NOTICES_PREVIEW, data, {}, { timeout: 300000 })
}

export const startRateNoticeRun = (data: RateNoticeScope) => {
  return Api.post(API_REPORTS_RATE_NOTICES_GENERATE, data)
}

export const getRateNoticeStatus = (jobId: string) => {
  return Api.get(API_REPORTS_RATE_NOTICES_STATUS, {}, { jobId })
}

export const cancelRateNoticeRun = (jobId: string) => Api.post(API_REPORTS_RATE_NOTICES_CANCEL, {}, { jobId })

export const downloadRateNotices = async (jobId: string) => {
  const res: Blob = await Api.get(
    API_REPORTS_RATE_NOTICES_DOWNLOAD,
    {},
    { jobId },
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile('Rate and Payment Change Notices.pdf', res)
}

// ---- Borrower Late Notices ----
export const getLateNoticeConfig = (): Promise<{
  success: number
  levels: Array<{ key: string; label: string; description: string; minDays: number; defaultTemplate: string }>
  templates: string[]
  docgenAvailable: boolean
  templateMap: Record<string, string>
}> => {
  return Api.get(API_REPORTS_LATE_NOTICES_CONFIG)
}

// Persist the level -> template mapping on its own. The generate modal calls this as a template is
// picked, so the choice outlives the dialog; nothing else on the schedule is touched.
export const saveLateNoticeTemplates = (templateMap: Record<string, string>) => {
  return Api.put(API_REPORTS_LATE_NOTICES_TEMPLATES, { templateMap })
}

export type NoticeKind = 'late' | 'insurance' | 'balloon'

export interface NoticeTemplatePreview {
  success: number
  message?: string
  /** The rendered notice, base64 PDF. */
  pdf?: string
  loanNumber?: number
  loanNo?: string
  borrower?: string
  level?: string
  template?: string
  language?: string
  /** False when the loan is outside the notice window, so the notice's own figures print blank. */
  inWindow?: boolean
}

/**
 * One notice rendered against a real loan, so a template can be seen before a run sends it. With no
 * loan number the server picks a loan on that level. Writes nothing.
 */
export const previewNoticeTemplate = (
  kind: NoticeKind,
  data: { level: string; template: string; loanNumber?: number },
): Promise<NoticeTemplatePreview> => {
  const url = {
    late: API_REPORTS_LATE_NOTICES_TEMPLATE_PREVIEW,
    insurance: API_REPORTS_INSURANCE_NOTICES_TEMPLATE_PREVIEW,
    balloon: API_REPORTS_BALLOON_NOTICES_TEMPLATE_PREVIEW,
  }[kind]
  return Api.post(url, data, {}, { timeout: 120000 })
}

export interface LateNoticePreviewRow {
  loanNumber: number
  /** The prior servicer's number for the loan. Blank when the loan carries none. */
  priorLoanNumber: string
  borrower: string
  daysLate: number
  nextPayment: string
  paymentsPastDue: number
  regularPayment: string
  totalAmountDue: string
  level: string
  levelKey: string
  /** Blank when the loan's level has no template mapped, so the run would skip it. */
  template: string
}

/**
 * A notice level the run would skip, and how many delinquent borrowers sit on it. The level filter
 * is otherwise silent: turning a rung off drops its borrowers from the review with nothing to show.
 */
export interface LateNoticeExcludedLevel {
  key: string
  label: string
  description: string
  count: number
}

/** The delinquent borrowers a run would cover. Writes nothing, so it is safe to re-run. */
export const previewLateNotices = (data: {
  asOf?: string
  tags?: string[]
  tagMatch?: TagMatch
  excludeTags?: string[]
  templateMap?: Record<string, string>
  levels?: string[]
  minDaysLate?: number
}): Promise<{
  success: number
  total: number
  rows: LateNoticePreviewRow[]
  excludedByLevel?: LateNoticeExcludedLevel[]
}> => {
  return Api.post(API_REPORTS_LATE_NOTICES_PREVIEW, data, {}, { timeout: 300000 })
}

export const startLateNoticeRun = (data: {
  asOf?: string
  tags?: string[]
  tagMatch?: TagMatch
  excludeTags?: string[]
  templateMap: Record<string, string>
  levels?: string[]
  minDaysLate?: number
  loanNumbers?: number[]
  attach?: boolean
  email?: boolean
}) => {
  return Api.post(API_REPORTS_LATE_NOTICES_GENERATE, data)
}

export const getLateNoticeStatus = (jobId: string) => {
  return Api.get(API_REPORTS_LATE_NOTICES_STATUS, {}, { jobId })
}

export const downloadLateNotices = async (jobId: string) => {
  const res: Blob = await Api.get(
    API_REPORTS_LATE_NOTICES_DOWNLOAD,
    {},
    { jobId },
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile('Borrower Late Notices.pdf', res)
}

export const getLateNoticeSchedule = (): Promise<any> => {
  return Api.get(API_REPORTS_LATE_NOTICES_SCHEDULE)
}

export const saveLateNoticeSchedule = (data: {
  frequency: Record<string, any>
  enabled: boolean
  templateMap: Record<string, string>
  tags: string[]
  tagMatch?: TagMatch
  excludeTags?: string[]
  minDaysLate?: number | null
  attach: boolean
  email: boolean
}) => {
  return Api.put(API_REPORTS_LATE_NOTICES_SCHEDULE, data)
}

export const runLateNoticeScheduleNow = () => {
  return Api.post(API_REPORTS_LATE_NOTICES_SCHEDULE_RUN, {}, {}, { timeout: 0 })
}

// ---- Insurance Expiration Notices ----
export const getInsuranceNoticeConfig = (): Promise<{
  success: number
  levels: Array<{ key: string; label: string; description: string; minDays: number; defaultTemplate: string }>
  templates: string[]
  docgenAvailable: boolean
  templateMap: Record<string, string>
}> => {
  return Api.get(API_REPORTS_INSURANCE_NOTICES_CONFIG)
}

// Persist the level -> template mapping on its own. The generate modal calls this as a template is
// picked, so the choice outlives the dialog; nothing else on the schedule is touched.
export const saveInsuranceNoticeTemplates = (templateMap: Record<string, string>) => {
  return Api.put(API_REPORTS_INSURANCE_NOTICES_TEMPLATES, { templateMap })
}

export const startInsuranceNoticeRun = (data: {
  asOf?: string
  tags?: string[]
  tagMatch?: TagMatch
  excludeTags?: string[]
  templateMap: Record<string, string>
  levels?: string[]
  attach?: boolean
  email?: boolean
}) => {
  return Api.post(API_REPORTS_INSURANCE_NOTICES_GENERATE, data)
}

export const getInsuranceNoticeStatus = (jobId: string) => {
  return Api.get(API_REPORTS_INSURANCE_NOTICES_STATUS, {}, { jobId })
}

export const downloadInsuranceNotices = async (jobId: string) => {
  const res: Blob = await Api.get(
    API_REPORTS_INSURANCE_NOTICES_DOWNLOAD,
    {},
    { jobId },
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile('Insurance Expiration Notices.pdf', res)
}

export const getInsuranceNoticeSchedule = (): Promise<any> => {
  return Api.get(API_REPORTS_INSURANCE_NOTICES_SCHEDULE)
}

export const saveInsuranceNoticeSchedule = (data: {
  frequency: Record<string, any>
  enabled: boolean
  templateMap: Record<string, string>
  tags: string[]
  tagMatch?: TagMatch
  excludeTags?: string[]
  attach: boolean
  email: boolean
}) => {
  return Api.put(API_REPORTS_INSURANCE_NOTICES_SCHEDULE, data)
}

export const runInsuranceNoticeScheduleNow = () => {
  return Api.post(API_REPORTS_INSURANCE_NOTICES_SCHEDULE_RUN, {}, {}, { timeout: 0 })
}

// ---- Balloon Payment Notices ----
export const getBalloonNoticeConfig = (): Promise<{
  success: number
  levels: Array<{ key: string; label: string; description: string; minDays: number; defaultTemplate: string }>
  templates: string[]
  docgenAvailable: boolean
  templateMap: Record<string, string>
}> => {
  return Api.get(API_REPORTS_BALLOON_NOTICES_CONFIG)
}

// Persist the level -> template mapping on its own. The generate modal calls this as a template is
// picked, so the choice outlives the dialog; nothing else on the schedule is touched.
export const saveBalloonNoticeTemplates = (templateMap: Record<string, string>) => {
  return Api.put(API_REPORTS_BALLOON_NOTICES_TEMPLATES, { templateMap })
}

export const startBalloonNoticeRun = (data: {
  asOf?: string
  tags?: string[]
  tagMatch?: TagMatch
  excludeTags?: string[]
  templateMap: Record<string, string>
  levels?: string[]
  attach?: boolean
  email?: boolean
}) => {
  return Api.post(API_REPORTS_BALLOON_NOTICES_GENERATE, data)
}

export const getBalloonNoticeStatus = (jobId: string) => {
  return Api.get(API_REPORTS_BALLOON_NOTICES_STATUS, {}, { jobId })
}

export const downloadBalloonNotices = async (jobId: string) => {
  const res: Blob = await Api.get(
    API_REPORTS_BALLOON_NOTICES_DOWNLOAD,
    {},
    { jobId },
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile('Balloon Payment Notices.pdf', res)
}

export const getBalloonNoticeSchedule = (): Promise<any> => {
  return Api.get(API_REPORTS_BALLOON_NOTICES_SCHEDULE)
}

export const saveBalloonNoticeSchedule = (data: {
  frequency: Record<string, any>
  enabled: boolean
  templateMap: Record<string, string>
  tags: string[]
  tagMatch?: TagMatch
  excludeTags?: string[]
  attach: boolean
  email: boolean
}) => {
  return Api.put(API_REPORTS_BALLOON_NOTICES_SCHEDULE, data)
}

export const runBalloonNoticeScheduleNow = () => {
  return Api.post(API_REPORTS_BALLOON_NOTICES_SCHEDULE_RUN, {}, {}, { timeout: 0 })
}

// Generate a servicing report with options (as-of date, tag filter, + any report-specific options).
export const downloadServicingReportWithOptions = async (data: {
  type: string
  asOf?: string
  filters?: { tags?: string[]; tagMatch?: TagMatch; excludeTags?: string[] }
  format?: 'pdf' | 'xlsx'
  [key: string]: any
}) => {
  const format = data.format === 'xlsx' ? 'xlsx' : 'pdf'
  const res: Blob = await Api.post(
    API_REPORTS_SERVICING,
    { ...data, format },
    {},
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile(`${data.type || 'Servicing Report'}.${format}`, res)
}

/** A background servicing-report run, as the server reports it. */
export interface IServicingReportJob {
  jobId: string
  type: string
  format: 'pdf' | 'xlsx'
  fileName: string
  status: 'queued' | 'running' | 'done' | 'error' | 'canceled'
  /** Runs ahead of this one in the single run lane; 0 or absent once it is working. */
  queuedBehind?: number
  startedAt: number
  finishedAt?: number
  bytes?: number
  error?: string
}

/**
 * Start the same report detached from the request.
 *
 * The synchronous call above holds a connection open for the minutes a portfolio-wide report takes,
 * which is both a bad experience and impossible to call off. This returns a job id to poll instead.
 */
export const startServicingReportJob = (data: {
  type: string
  asOf?: string
  filters?: { tags?: string[]; tagMatch?: TagMatch }
  format?: 'pdf' | 'xlsx'
  [key: string]: any
}): Promise<{ success: number; jobId?: string; message?: string }> => {
  return Api.post(API_REPORTS_SERVICING_JOBS, data)
}

/** This user's recent runs, so a reload picks the background tray back up. */
export const getServicingReportJobs = (): Promise<{ success: number; jobs: IServicingReportJob[] }> => {
  return Api.get(API_REPORTS_SERVICING_JOBS)
}

export const getServicingReportJob = (
  jobId: string,
): Promise<{ success: number; message?: string; job?: IServicingReportJob }> => {
  return Api.get(API_REPORTS_SERVICING_JOB, {}, { jobId })
}

/** Call off a run. It stops at its next checkpoint and its output is discarded. */
export const cancelServicingReportJob = (jobId: string) => {
  return Api.post(API_REPORTS_SERVICING_JOB_CANCEL, {}, { jobId })
}

/** Drop a finished run from the tray and release the file it is holding. */
export const dismissServicingReportJob = (jobId: string) => {
  return Api.delete(API_REPORTS_SERVICING_JOB, {}, { jobId })
}

/** Pull down a finished run's file. */
export const downloadServicingReportJobFile = async (jobId: string, fileName: string) => {
  const res: Blob = await Api.get(
    API_REPORTS_SERVICING_JOB_DOWNLOAD,
    {},
    { jobId },
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile(fileName, res)
}

/**
 * The NMLS Mortgage Call Report as an FV6 upload file.
 *
 * A separate endpoint from the servicing report download because it is a different artifact: the
 * PDF is what a preparer reads, this is what NMLS ingests.
 */
export const downloadMortgageCallXml = async (period: string, includeFc = true) => {
  const res: Blob = await Api.get(
    API_REPORTS_MCR_XML,
    { period, includeFc: includeFc ? 'true' : 'false' },
    {},
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile(`mcr-fv6-${period || 'current'}.xml`, res)
}

/** What the upload would contain — states covered, schema exceptions, lines left for the preparer. */
export const getMortgageCallXmlPreview = (
  period: string,
  includeFc = true,
): Promise<{
  success: number
  data: {
    fileName: string
    states: string[]
    bytes: number
    errors: Array<{ field: string; value: any; reason: string }>
    omitted: string[]
    readiness: string[]
  }
}> => {
  return Api.get(API_REPORTS_MCR_XML_PREVIEW, { period, includeFc: includeFc ? 'true' : 'false' })
}

// Report-specific option inputs for the servicing report options dialog.
export const getServicingReportOptions = (
  type: string,
): Promise<{ success: number; options: Array<Record<string, any>> }> => {
  return Api.get(API_REPORTS_SERVICING_OPTIONS, { type })
}

// Per-loan purchase-discount record (powers the Earned Discount / Fees report).
export const getLoanDiscount = (
  loanNumber: number | string,
): Promise<{ loanNumber: number; discountAmount: number; purchaseDate: string; amortizationEndDate: string }> => {
  return Api.get(API_REPORTS_LOAN_DISCOUNT, {}, { loanNumber })
}

export const saveLoanDiscount = (
  loanNumber: number | string,
  data: { discountAmount?: number; purchaseDate?: string; amortizationEndDate?: string },
) => {
  return Api.put(API_REPORTS_LOAN_DISCOUNT, data, { loanNumber })
}

export const getLoanReportsPlaceholders = (): Promise<Record<string, any[]>> => {
  return Api.get(API_REPORTS_PLACEHOLDERS)
}

export const getLoanDetailsReportFieldValues = (headers: string[]): Promise<Record<string, any>> => {
  return Api.post(API_REPORTS_LOAN_DETAILS_FIELD_VALUES, { headers })
}

export const getLoanReports = () => {
  return Api.get(API_REPORTS)
}

export const getLoanReport = (id: number) => {
  return Api.get(API_REPORTS_BY_ID, {}, { id })
}

export const generateReportsPreview = (data: {
  type: string
  id: string
  headers: string[]
  filters: Record<string, any>
  requiredHeaders?: string[]
  skip?: number
  count?: number
}) => {
  return Api.post(API_REPORTS_PREVIEW, data)
}

export const downloadReports = async (data: {
  type: string
  id: string
  title: string
  headers: string[]
  filters: Record<string, any>
  requiredHeaders?: string[]
  fileName?: string
}) => {
  const res: Blob = await Api.post(
    API_REPORTS_DOWNLOAD,
    data,
    {},
    {
      responseType: 'blob',
      timeout: 300000,
    },
  )

  const { title, fileName } = data
  downloadFile(fileName || `Loan Report${title ? ` - ${title}` : ''}.xlsx`, res)
}

export const downloadReportsCSV = async (data: {
  type: string
  id: string
  title: string
  headers: string[]
  filters: Record<string, any>
  requiredHeaders?: string[]
  fileName?: string
}) => {
  const res: Blob = await Api.post(
    API_REPORTS_CSV_DOWNLOAD,
    data,
    {},
    {
      responseType: 'blob',
      timeout: 300000,
    },
  )

  const { title, fileName } = data

  downloadFile(fileName || `Loan Report${title ? ` - ${title}` : ''}.csv`, res)
}

export interface IHmdaPipeJob {
  jobId: string
  fileName: string
  status: 'queued' | 'running' | 'done' | 'error' | 'canceled'
  startedAt: number
  queuedBehind?: number
  finishedAt?: number
  bytes?: number
  handled: number
  total: number
  error?: string
}

/**
 * Start the HMDA filing file on the background lane.
 *
 * The file used to be built inside this request, which for a full book held the connection and
 * loaded the web task until every loan was written. The server returns a job id; the tray polls it.
 */
export const startHmdaPipeJob = (data: {
  type: string
  id: string
  title?: string
  headers?: string[]
  filters: Record<string, any>
  requiredHeaders?: string[]
  origin?: number
}): Promise<{ success: number; jobId?: string; message?: string }> => {
  return Api.post(API_REPORTS_HMDA_PIPE_JOBS, data)
}

/** This user's recent HMDA pipe runs, so a reload picks the background tray back up. */
export const getHmdaPipeJobs = (): Promise<{ success: number; jobs: IHmdaPipeJob[] }> => {
  return Api.get(API_REPORTS_HMDA_PIPE_JOBS)
}

export const getHmdaPipeJob = (jobId: string): Promise<{ success: number; message?: string; job?: IHmdaPipeJob }> => {
  return Api.get(API_REPORTS_HMDA_PIPE_JOB, {}, { jobId })
}

export const cancelHmdaPipeJob = (jobId: string) => {
  return Api.post(API_REPORTS_HMDA_PIPE_JOB_CANCEL, {}, { jobId })
}

export const dismissHmdaPipeJob = (jobId: string) => {
  return Api.delete(API_REPORTS_HMDA_PIPE_JOB, {}, { jobId })
}

export const downloadHmdaPipeJobFile = async (jobId: string, fileName?: string) => {
  const res: Blob = await Api.get(
    API_REPORTS_HMDA_PIPE_JOB_DOWNLOAD,
    {},
    { jobId },
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile(fileName || 'HMDA Report.txt', res)
}

/** @deprecated The pipe file is a background job. Starts the job and returns its id. */
export const downloadReportsPipe = startHmdaPipeJob

export const saveReports = (id: number, data: Record<string, any>) => {
  return Api.put(API_REPORTS_BY_ID, data, { id })
}

export const removeLoanReport = (id: number) => {
  return Api.delete(API_REPORTS_BY_ID, {}, { id })
}

// ---- Calling off a notice run ----
// All three ladders stop between borrowers; the notices already generated are kept.
export const cancelLateNoticeRun = (jobId: string) => Api.post(API_REPORTS_LATE_NOTICES_CANCEL, {}, { jobId })
export const cancelInsuranceNoticeRun = (jobId: string) => Api.post(API_REPORTS_INSURANCE_NOTICES_CANCEL, {}, { jobId })
export const cancelBalloonNoticeRun = (jobId: string) => Api.post(API_REPORTS_BALLOON_NOTICES_CANCEL, {}, { jobId })
